/**
 * Right Sidebar Tabs Component
 * Tab bar for switching between Members, Media, Files, Links in the always-on sidebar
 */

import React from 'react';

export type SidebarTab = 'members' | 'media' | 'files' | 'links';

interface RightSidebarTabsProps {
  activeTab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;
  showMembers?: boolean;
  counts?: {
    members?: number;
    media?: number;
    files?: number;
    links?: number;
  };
}

const tabs: { key: SidebarTab; label: string; requiresMembers?: boolean }[] = [
  { key: 'members', label: 'Членове', requiresMembers: true },
  { key: 'media', label: 'Медия' },
  { key: 'files', label: 'Файлове' },
  { key: 'links', label: 'Линкове' },
];

export const RightSidebarTabs: React.FC<RightSidebarTabsProps> = ({
  activeTab,
  onTabChange,
  showMembers = true,
  counts = {},
}) => {
  const visibleTabs = showMembers ? tabs : tabs.filter(t => !t.requiresMembers);

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = (index + dir + visibleTabs.length) % visibleTabs.length;
    onTabChange(visibleTabs[next].key);
  };

  return (
    <div
      role="tablist"
      aria-label="Информация за разговора"
      className="flex border-b border-gray-200 dark:border-dark-border-primary bg-white dark:bg-dark-bg-secondary"
    >
      {visibleTabs.map((tab, index) => {
        const count = counts[tab.key];
        const isActive = activeTab === tab.key;

        return (
          <button
            key={tab.key}
            id={`sidebar-tab-${tab.key}`}
            role="tab"
            aria-selected={isActive}
            aria-controls="sidebar-tabpanel"
            tabIndex={isActive ? 0 : -1}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onClick={() => onTabChange(tab.key)}
            className={`flex-1 px-2 py-2.5 text-xs font-medium transition-colors relative ${
              isActive
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
            }`}
          >
            <span>{tab.label}</span>
            {count !== undefined && count > 0 && (
              <span className={`ml-1 text-[10px] ${
                isActive ? 'text-primary-500 dark:text-primary-400' : 'text-gray-400 dark:text-dark-text-muted'
              }`}>
                {count}
              </span>
            )}
            {isActive && (
              <div className="absolute bottom-0 left-1 right-1 h-0.5 bg-primary-500 dark:bg-primary-400 rounded-full" />
            )}
          </button>
        );
      })}
    </div>
  );
};
